import './App.css';
import SiteStateManager from './SiteState/SiteStateManager';
import { useState, useEffect } from 'react';

const ANIMATION_DURATION = 8000; // 8 seconds in milliseconds
const LINES_PER_SET = 4;
const COLORS = [
  '#FF0000', '#FF3300', '#FF6600', '#FF9900',
  '#FFCC00', '#00FF00', '#00CC00', '#0099FF',
  '#0066FF', '#0033FF', '#6600FF', '#9900FF',
  '#CC00FF', '#FF00FF', '#FF0099', '#FF0066'
];

function LineSet({ yCoordinate, direction, count, colorIndex }) {
  const lines = Array.from({ length: count }, (_, i) => {
    const startPos = direction === 'rtl' ? '100vw' : '-10vw';
    // Stagger the animation so each line wraps at different times
    const delay = -(i * 2); // 2s delay between each line
    const color = colorIndex >= 0 ? COLORS[colorIndex] : '#444444';
    return (
      <div
        key={i}
        className={`moving-line ${direction}`}
        style={{
          top: `${yCoordinate}%`,
          '--start-pos': startPos,
          '--delay': `${delay}s`,
          '--line-color': color,
          '--glow-color': color
        }}
      ></div>
    );
  });

  return lines;
}

const COLOR_COOKIE = 'lineColorIndex';

function getSavedColorIndex() {
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${COLOR_COOKIE}=`));

  if (!match) return -1;

  const value = Number(match.split('=')[1]);

  return Number.isInteger(value) && value >= 0 && value < COLORS.length
    ? value
    : -1;
}

function saveColorIndex(index) {
  document.cookie = `${COLOR_COOKIE}=${index}; max-age=31536000; path=/; SameSite=Lax`;
}

function App() {
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime] = useState(Date.now());
  const [pausedTime, setPausedTime] = useState(0);
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [colorIndex, setColorIndex] = useState(getSavedColorIndex);
  const [isCyclingColors, setIsCyclingColors] = useState(false);
  const [colorCycleTimer, setColorCycleTimer] = useState(null);

  useEffect(() => {
    if (colorIndex >= 0) {
      saveColorIndex(colorIndex);
    }
  }, [colorIndex]);

  useEffect(() => {
    const handleSpacebar = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (isPaused) {
          // Resuming - add the pause duration to total paused time
          if (pauseStartTime) {
            setPausedTime(pausedTime + (Date.now() - pauseStartTime));
            setPauseStartTime(null);
          }
        } else {
          // Pausing - mark when pause started
          setPauseStartTime(Date.now());
        }
        setIsPaused(!isPaused);
      }
    };

    window.addEventListener('keydown', handleSpacebar);
    return () => window.removeEventListener('keydown', handleSpacebar);
  }, [isPaused, pausedTime, pauseStartTime]);

  useEffect(() => {
    let animationFrameId;

    const animationFrame = () => {
      if (!isPaused) {
        const now = Date.now();
        const totalElapsed = now - startTime - pausedTime;
        setElapsedTime(totalElapsed % ANIMATION_DURATION);
      }
      animationFrameId = requestAnimationFrame(animationFrame);
    };

    animationFrameId = requestAnimationFrame(animationFrame);
    return () => cancelAnimationFrame(animationFrameId);
  }, [startTime, pausedTime, isPaused]);

  const calculateAlignment = () => {
    const cycleTime = ANIMATION_DURATION / LINES_PER_SET;
    const progress = (elapsedTime % cycleTime) / cycleTime;

    // LTR lines move from -10vw to 100vw (110vw total)
    const ltrPos = -10 + progress * 110;

    // RTL lines move from 100vw to -10vw (110vw total, moving left)
    const rtlPos = 100 - progress * 110;

    // Calculate alignment (0-100) where 100 is perfectly aligned
    // Maximum distance is 110vw (start position difference)
    const distance = Math.abs(ltrPos - rtlPos);
    const maxDistance = 110;
    const alignment = Math.round(100 * (1 - distance / maxDistance));

    return Math.max(0, alignment);
  };

  // Handle color cycling when paused and alignment is outside 5-10%
  useEffect(() => {
    if (!isPaused) {
      setIsCyclingColors(false);
      if (colorCycleTimer) clearInterval(colorCycleTimer);
      // Keep the final color, don't reset to -1
      return;
    }

    const alignment = calculateAlignment();
    if (alignment <= .25 || alignment >= 99.75) {
      if (!isCyclingColors) {
        setIsCyclingColors(true);
        const totalSteps = Math.floor(Math.random() * (32 - 16 + 1)) + 16;
        let index = 0;
        const timer = setInterval(() => {
          if (index < totalSteps) {
            setColorIndex(index % COLORS.length);
            index++;
          } else {
            clearInterval(timer);
          }
        }, 50);
        setColorCycleTimer(timer);
      }
    } else {
      setIsCyclingColors(false);
      if (colorCycleTimer) clearInterval(colorCycleTimer);
    }

    return () => {
      if (colorCycleTimer) clearInterval(colorCycleTimer);
    };
  }, [isPaused, elapsedTime]);

  // return (
  //   <div className={`App ${isPaused ? 'paused' : ''}`}>
  //     {Array.from({ length: 22 }, (_, i) => {
  //       const yCoord = 100 - (i * 5);
  //       const direction = i % 2 === 0 ? 'rtl' : 'ltr';
  //       return <LineSet key={i} yCoordinate={yCoord} direction={direction} count={4} colorIndex={colorIndex} />;
  //     })}
  //     <div className="phone-number">(814) 815-LIVE</div>
  //   </div>
  // );

  return (
    <SiteStateManager />
  );

}

export default App;

